/**
 * A socket token that is never stale when Phoenix asks for it.
 *
 * Phoenix reads a socket's `params` synchronously each time it reconnects, so
 * a token has to be in hand before the line drops, not fetched after it. The
 * call socket used to be built with the token it started with, and a meeting
 * that outlived that token could not reconnect at all. This keeps one fresh
 * on a timer at half its lifetime — the same rhythm `RelaiiSocket` keeps for
 * the main socket — and fetches another whenever asked.
 */

import { api } from '@/api/client'

interface SocketTokenResponse {
  token: string
  expires_in: number
}

const RETRY_MS = 5_000
const MIN_REFRESH_MS = 15_000

export class SocketTokenKeeper {
  private token: string | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private inFlight: Promise<string> | null = null
  private stopped = false

  constructor(
    private readonly fetchToken: () => Promise<SocketTokenResponse> = () =>
      api.post<SocketTokenResponse>('/auth/socket-token'),
  ) {}

  /** The token to connect with. Null only before the first one has arrived. */
  current(): string | null {
    return this.token
  }

  /**
   * Fetches a token now and schedules the next. Callers that ask at the same
   * time share one request. A failure keeps the token already held and tries
   * again shortly.
   */
  refresh(): Promise<string> {
    if (this.inFlight !== null) return this.inFlight

    const pending = this.fetchToken()
      .then(
        ({ token, expires_in }) => {
          if (!this.stopped) {
            this.token = token
            this.schedule(Math.max(MIN_REFRESH_MS, Math.floor(expires_in / 2) * 1000))
          }
          return token
        },
        (caught: unknown) => {
          this.schedule(RETRY_MS)
          throw caught
        },
      )
      .finally(() => {
        this.inFlight = null
      })

    this.inFlight = pending
    return pending
  }

  stop(): void {
    this.stopped = true
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
  }

  private schedule(delayMs: number): void {
    if (this.stopped) return
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.timer = null
      void this.refresh().catch(() => undefined)
    }, delayMs)
  }
}
